import type { StorageService } from '@/services/storage/types';

import { ATTRIBUTION_STORAGE_KEY, type AttributionIdentifiers } from './types';

/**
 * Persists AttributionIdentifiers via StorageService only (no direct localStorage).
 */
export class AttributionStore {
  private readonly storage: StorageService;

  private readonly persist: boolean;

  private readonly ttlMs: number;

  private readonly storageKey: string;

  constructor(
    storage: StorageService,
    options: { persist: boolean; ttlDays: number; storageKey?: string }
  ) {
    this.storage = storage;
    this.persist = options.persist;
    this.ttlMs = options.ttlDays * 24 * 60 * 60 * 1000;
    this.storageKey = options.storageKey ?? ATTRIBUTION_STORAGE_KEY;
  }

  load(): AttributionIdentifiers | null {
    if (!this.persist) return null;

    try {
      const snapshot = this.storage.get<AttributionIdentifiers>(
        this.storageKey
      );
      if (!snapshot) return null;

      if (this.isExpired(snapshot)) {
        this.clear();
        return null;
      }

      return snapshot;
    } catch {
      return null;
    }
  }

  save(snapshot: AttributionIdentifiers): void {
    if (!this.persist) return;

    try {
      this.storage.set(this.storageKey, snapshot);
    } catch {
      // quota / SSR — keep in-memory only
    }
  }

  clear(): void {
    try {
      this.storage.remove(this.storageKey);
    } catch {
      // ignore
    }
  }

  private isExpired(snapshot: AttributionIdentifiers): boolean {
    const anchor = snapshot.lastCollectedAt ?? snapshot.firstCollectedAt;
    if (!anchor) return false;
    return Date.now() - anchor > this.ttlMs;
  }
}
